import { computed, ref } from '@vue/composition-api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { TariffException, ITariffExceptionData } from '@/models';
import { MaybeRef } from '@tanstack/vue-query/build/lib/types';
import providerInstance from '@/services/provider';

export function useFetchTariffExceptions() {
  const queryKey = ref(['tariff-exceptions']);

  const { isLoading, isError, error, data: tariffExceptions } = useQuery({
    queryKey: queryKey,
    queryFn: () => providerInstance.tariff.fetchTariffExceptions(),
    enabled: true,
    select: data => {
      return data.map(tariffException => new TariffException(tariffException));
    }
  });

  return { queryKey, tariffExceptions, error, isError, isLoading };
}

export function useFetchTariffException(id: MaybeRef<number>) {
  const tariffExceptionIdRef = ref(id);

  const queryKey = ref(['tariff-exceptions', tariffExceptionIdRef]);

  const { data: tariffException, isLoading, error } = useQuery({
    queryKey: queryKey,
    enabled: computed(() => Boolean(tariffExceptionIdRef.value)),
    queryFn: () =>
      providerInstance.tariff.fetchTariffException(tariffExceptionIdRef.value),
    select: data => new TariffException(data)
  });

  return { tariffException, isLoading, error, queryKey };
}

export function useCreateTariffException() {
  const queryClient = useQueryClient();

  const {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    mutateAsync,
    reset
  } = useMutation({
    mutationKey: ['create-tariff-exception'],
    mutationFn: (tariffException: ITariffExceptionData) =>
      providerInstance.tariff.createTariffException(tariffException),

    onSuccess: data => {
      queryClient.setQueryData(['tariff-exceptions', data.id], data);
      queryClient.invalidateQueries({ queryKey: ref(['tariff-exceptions']) });
    }
  });
  return {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    mutateAsync,
    reset
  };
}

export function useUpdateTariffException() {
  const queryClient = useQueryClient();

  const { isLoading, isError, error, isSuccess, mutate, reset } = useMutation({
    mutationKey: ['update-tariff-exception'],
    mutationFn: (tariffException: ITariffExceptionData) =>
      providerInstance.tariff.updateTariffException(tariffException),

    onSuccess: data => {
      queryClient.setQueryData(['tariff-exceptions', data.id], data);
      queryClient.invalidateQueries({
        queryKey: ref(['tariff-exceptions', data.id])
      });
      queryClient.invalidateQueries({ queryKey: ref(['tariff-exceptions']) });
    }
  });
  return {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    reset
  };
}

export function useDeleteTariffException() {
  const mutationKey = ref(['delete-tariff-exception']);

  const queryClient = useQueryClient();

  const { isLoading, isError, error, isSuccess, mutate, reset } = useMutation({
    mutationKey: mutationKey.value,
    mutationFn: (id: number) =>
      providerInstance.tariff.deleteTariffException(id),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ref(['tariff-exceptions']) });
    }
  });
  return {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    reset
  };
}

export function useUploadTariffExceptions() {
  const queryClient = useQueryClient();

  const { isLoading, isError, error, isSuccess, mutate, reset } = useMutation({
    mutationKey: ['upload-Tariff-exceptions'],
    mutationFn: (file: File) =>
      providerInstance.tariff.uploadTariffExceptions(file),

    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ref(['tariff-exceptions']) });
    }
  });

  return {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    reset
  };
}
